import { createContext, useContext, useEffect, useState } from "react";
import { UserDetails } from "../types";
import accountApi from "../api/account/accountApi";
import { LoginResponse } from "../api/account/types";

interface AuthContextType {
    user: UserDetails | null;
    loading: boolean;

    refreshUser: () => Promise<void>;
    logIn: (email: string, password: string) => Promise<LoginResponse>;
    logOut: (keepSession?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserDetails | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        refreshUser().finally(() => setLoading(false));
    }, []);

    const refreshUser = async () => {
        await accountApi.me()
            .then(setUser)
            .catch(() => setUser(null));
    };

    const logIn = async (email: string, password: string) => {
        const resp = await accountApi.logIn(email, password);
        if (resp.success) {
            setUser(resp.userDetails);
        }
        return resp;
    }

    const logOut = async (keepSession: boolean = false) => {
        await accountApi.logOut(keepSession);
        setUser(null);
    }

    return <AuthContext.Provider value={{ user, loading, refreshUser, logIn, logOut }}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside an AuthProvider");
    }
    return ctx;
}
